import { gql } from "@apollo/client";
// eslint-disable-next-line import/no-extraneous-dependencies
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  AtlasAvatarGroupProps,
  AvatarGroup,
  Icon,
  useAvatarGroupItems,
  useMenuItems,
  useToast,
} from "@resource/atlas";
import { INSERT_TARGETS } from "@resource/atlas/content-editor/__utils/insert-options-targets-order";
import {
  createRichBlock,
  RichBlockProps,
} from "@resource/atlas/content-editor/__utils/rich-blocks";
import {
  atlasCalendarAlt,
  atlasRefreshCw,
  atlasTrash,
  serviceZoom,
} from "@resource/atlas/icons";
import { useFlags } from "@resource/client-ffs";
import { strings } from "@resource/common";
import { useLogEvent } from "analytics";
import { CalendarIcon } from "client/components/guide/interviews/CalendarIcon";
import { EditableInterviewList } from "client/components/guide/interviews/EditableInterviewList";
import { InterviewList } from "client/components/guide/interviews/InterviewList";
import { useFormattedDateTimesForInterviewItem } from "client/components/guide/interviews/utils";
import { useGuideIdContext } from "client/utils/guide-id-provider";
import { formatPersonName } from "client/utils/strings";
import { useFlagsLoading } from "components/IdentifyUserWrapper";
import FeatureFlags from "generated/FeatureFlags";
import {
  GuideInterviewsFromRichBlockContext,
  GuideInterviewsFromRichBlockContextVariables,
  GuideUpcomingInterviewsForRichBlock,
  GuideUpcomingInterviewsForRichBlockVariables,
  InterviewsRichBlockInterviewFragment as Interview,
} from "generated/schemaTypes";
import { $getNodeByKey } from "lexical";
import { chain, map, reject } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import {
  DEFAULT_DATA,
  InterviewsData,
  InterviewsName,
} from "shared/guide-content/rich-blocks/interviews";
import { useResponsiveValue } from "utils/responsive";
import useQuery from "utils/useQuery";

import { useCandidateTimezoneContext } from "../../candidate-timezone-provider";
import {
  AttachedFilesRichBlockProvider,
  useAttachedFiles,
} from "./attached-files-context";
import { useInterviewsConfig } from "./config";

const INTERVIEWS_RICH_BLOCK_INTERVIEW_FRAGMENT = gql`
  fragment InterviewsRichBlockInterviewFragment on Event {
    ...InterviewListInterviewFragment
    ...EditableInterviewListInterviewFragment
    icsFile(sendToEmail: $sendToEmail) @skip(if: $skipICSFiles) {
      ...AttachedFilesForRichBlocks
    }
  }
  ${InterviewList.fragments.interview}
  ${EditableInterviewList.fragments.interview}
  ${AttachedFilesRichBlockProvider.fragments.file}
`;

const GUIDE_UPCOMING_INTERVIEWS_FOR_RICH_BLOCK = gql`
  query GuideUpcomingInterviewsForRichBlock(
    $where: GuideWhereUniqueInput!
    $sendToEmail: String
    $skipICSFiles: Boolean!
  ) {
    guideFindUnique(where: $where) {
      id
      upcomingInterviewsV2 {
        ...InterviewsRichBlockInterviewFragment
      }
    }
  }
  ${INTERVIEWS_RICH_BLOCK_INTERVIEW_FRAGMENT}
`;

const GUIDE_INTERVIEWS_FROM_RICH_BLOCK_CONTEXT = gql`
  query GuideInterviewsFromRichBlockContext(
    $guideId: String!
    $interviewIds: [String!]!
    $sendToEmail: String
    $skipICSFiles: Boolean!
  ) {
    interviewsById(guideId: $guideId, interviewIds: $interviewIds) {
      ...InterviewsRichBlockInterviewFragment
    }
  }
  ${INTERVIEWS_RICH_BLOCK_INTERVIEW_FRAGMENT}
`;

function useInterviewCardMenuItemsFactory(
  stateData: InterviewsData,
  updateData: (
    valueOrFactory:
      | Partial<InterviewsData>
      | ((data: InterviewsData) => Partial<InterviewsData>)
  ) => void,
  nodeKey: string
) {
  return function useInterviewCardMenuItems(
    interviewId: string
  ): ReturnType<typeof useMenuItems> {
    const [editor] = useLexicalComposerContext();
    const logEvent = useLogEvent({
      component: "Interviews rich block",
      project: "Embed an interview schedule in a message",
    });
    return useMenuItems(
      (i) => [
        i.item({
          key: "remove",
          children: "Remove",
          leadingContent: <Icon content={atlasTrash} />,
          onClick: () => {
            logEvent("Remove a single interview from the rich block");
            const updatedInterviews = stateData.interviews.filter(
              (interview) => interview.id !== interviewId
            );
            if (updatedInterviews.length === 0) {
              // remove node if only interview is deleted
              editor.update(() => $getNodeByKey(nodeKey)?.remove());
            } else {
              updateData({
                interviews: updatedInterviews,
              });
            }
          },
        }),
      ],
      [editor, interviewId, logEvent]
    );
  };
}

export const useSyncICSFilesWithInterviews = ({
  interviews,
  nodeKey,
}: {
  interviews?: Interview[];
  nodeKey: string;
}) => {
  const { setAttachedFiles } = useAttachedFiles();
  useEffect(() => {
    const icsFiles = chain(interviews).map("icsFile").compact().value();

    setAttachedFiles((prev) =>
      chain(prev)
        .reject({ nodeKey })
        .concat(map(icsFiles, (icsFile) => ({ ...icsFile, nodeKey })))
        .sortBy("filename")
        .value()
    );

    // Remove all from this rich block on unmount
    return () => {
      setAttachedFiles((prev) => reject(prev, { nodeKey }));
    };
  }, [interviews, setAttachedFiles, nodeKey]);
};

const placeholderInterview = {
  id: "178eac8b-e1e2-4ffc-8431-e4f6bc580f4d",
  title: "Onsite interview with Engineering",
  description:
    "Join us for a virtual onsite as you meet with members of our engineering team.",
  startTime: "2022-01-19T15:00:00.000Z",
  hidden: false,
  endTime: "2022-01-19T16:00:00.000Z",
  conferenceUrl: "https://example.zoom.us/p/5555555555",
  interviewers: [
    {
      id: "b9597b92-1e38-4c2d-b375-e302788e0f8b",
      firstName: "Paul",
      lastName: "Atreides",
      title: "Muad'Dib",
      biography:
        "Paul is a highly charismatic and driven individual with a talent for inspiring those around him. With his combination of leadership skills, strategic thinking, and emotional intelligence, he's a valuable asset to our team.",
      websiteUrls: ["https://dunenovels.com"],
      videoIntroductionUrl: null,
    },
    {
      id: "b9597b92-1e38-4c2d-b375-e302788e0f8b",
      firstName: "Dominic",
      lastName: "Sherman",
      imageUrl:
        "https://lh3.googleusercontent.com/a-/AOh14Gi9AItPDgb8YS9TIIMEPBuf4tZ6hJTsck9PQLfr=s96",
      title: "Engineer",
      biography:
        "Dominic is a strong software engineer with a passion for leadership and problem solving.",
      websiteUrls: ["https://guide.co"],
      videoIntroductionUrl: null,
    },
  ],
  conferencePhone: null,
  location: null,
  collaborativeCodingUrl: null,
};

export function PlaceholderInterviewCard() {
  const logEvent = useLogEvent({
    component: "Interviews rich block",
    project: "Embed an interview schedule in a message",
  });
  const { interviewers } = placeholderInterview;

  useEffect(() => {
    logEvent("Create a placeholder interviews rich block");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedDateTime = useFormattedDateTimesForInterviewItem({
    interview: placeholderInterview,
    hideStartTimeNotification: true,
  });

  const avatarSize = useResponsiveValue<AtlasAvatarGroupProps["size"]>({
    xs: "small",
    md: "medium",
  });
  const interviewerAvatars = useAvatarGroupItems(
    (i) =>
      interviewers.map((interviewer) =>
        i.avatar({
          key: interviewer.id,
          name: formatPersonName(interviewer.firstName, interviewer.lastName),
          image: interviewer.imageUrl,
        })
      ),
    [interviewers]
  );
  const interviewerNames = useMemo(
    () =>
      strings.joinWithFinalSeparator(
        interviewers.map((interviewer) => interviewer.firstName)
      ),

    [interviewers]
  );

  return (
    <div className="border border-yellow-800 border-dashed rounded-md relative">
      <div className="absolute top-0 left-0 bg-yellow-50 border border-l-0 border-t-0 border-yellow-800 border-dashed rounded-[4px] px-1 flex items-center h-[18px]">
        <span className="text-yellow-900 font-bold font-mono text-[10px]">
          Example
        </span>
      </div>
      <div className="p-4 w-full relative">
        <div className="flex flex-col gap-3 md:gap-4">
          <header className="flex gap-3">
            <div className="flex-grow truncate">
              <div className="space-y-1 md:space-y-1 text-start">
                <div className="flex flex-col gap-1">
                  <p className="text-subtle text-body-sm md:text-body-md">
                    {formattedDateTime.formattedStartDate} •{" "}
                    {formattedDateTime.formattedTime}
                  </p>
                  <div className="text-body-md-heavy md:text-body-lg-heavy text-dark">
                    {placeholderInterview.title}
                  </div>
                  <div className="truncate w-full text-body-md text-dark">
                    {placeholderInterview.description}
                  </div>
                </div>
              </div>
            </div>
            <CalendarIcon
              className="flex-shrink-0"
              isInPast={false}
              day={formattedDateTime.day}
              dayName={formattedDateTime.dayName}
            />
          </header>
          <section className="flex gap-3 md:gap-4 flex-wrap">
            <div className="!text-[#008BFF] flex gap-2 items-center">
              <Icon content={serviceZoom} className="w-5 text-subtle" />
              <p className="text-body-md">Zoom Meeting</p>
            </div>
          </section>
          <section className="flex gap-[0.375rem] items-center">
            <AvatarGroup size={avatarSize} items={interviewerAvatars} />
            <p className="text-subtle text-body-md truncate">
              {interviewerNames}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

function MessageInterviewList({
  updateData,
  data: stateData,
  ConfigMenu,
  nodeKey,
  guideId,
}: RichBlockProps<InterviewsData> & { guideId: string }) {
  const { sendToast } = useToast();
  const { sendToEmail, enableICSFiles } = useAttachedFiles();
  const { lastFetchedAt, variant } = stateData;
  const {
    [FeatureFlags.ICS_FILES_IN_MESSAGES]: icsFilesInMessagesEnabled,
    [FeatureFlags.EDIT_INTERVIEW_IN_MODAL]: editInterviews,
  } = useFlags();
  const { loading: flagsLoading } = useFlagsLoading();

  const { onEdit } = useInterviewsConfig() ?? {};

  const logEvent = useLogEvent({
    component: "Interviews rich block",
    project: "Embed an interview schedule in a message",
  });

  useEffect(() => {
    logEvent("Create an interviews rich block");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: fromState } = useQuery<
    GuideInterviewsFromRichBlockContext,
    GuideInterviewsFromRichBlockContextVariables
  >(GUIDE_INTERVIEWS_FROM_RICH_BLOCK_CONTEXT, {
    variables: {
      guideId,
      interviewIds: stateData.interviews.map((interview) => interview.id),
      sendToEmail,
      skipICSFiles: !icsFilesInMessagesEnabled || !enableICSFiles,
    },
    skip: !lastFetchedAt || flagsLoading,
  });

  const { refetch, loading } = useQuery<
    GuideUpcomingInterviewsForRichBlock,
    GuideUpcomingInterviewsForRichBlockVariables
  >(GUIDE_UPCOMING_INTERVIEWS_FOR_RICH_BLOCK, {
    variables: {
      where: {
        id: guideId,
      },
      sendToEmail,
      skipICSFiles: !icsFilesInMessagesEnabled || !enableICSFiles,
    },
    onCompleted: (data) => {
      if (data.guideFindUnique) {
        updateData({
          lastFetchedAt: new Date().toISOString(),
          interviews: data.guideFindUnique.upcomingInterviewsV2 ?? [],
        });
      }
    },
    fetchPolicy: "network-only",
    skip:
      (variant === "upcoming" && !!lastFetchedAt) || !guideId || flagsLoading,
    pollInterval: 120000,
  });

  const refreshInterviews = useCallback(async () => {
    await refetch();

    sendToast("Upcoming interviews have been refreshed.", {
      variant: "success",
    });
  }, [refetch, sendToast]);

  const configMenuItems = useMenuItems(
    (i) => [
      i.item({
        key: "refresh",
        onClick: refreshInterviews,
        renderContent: ({ children }) => (
          <div className="flex gap-2 items-center">
            <Icon content={atlasRefreshCw} />
            <span>{children}</span>
          </div>
        ),
        children: "Refresh",
      }),
    ],
    [refreshInterviews]
  );

  const interviews = fromState?.interviewsById;

  useSyncICSFilesWithInterviews({ interviews, nodeKey });

  const useInterviewCardMenuItems = useInterviewCardMenuItemsFactory(
    stateData,
    updateData,
    nodeKey
  );

  return (
    <>
      <ConfigMenu items={configMenuItems} />
      <div className="confirm-interviews-block">
        {editInterviews ? (
          <EditableInterviewList
            interviews={loading ? undefined : interviews}
            type="upcoming"
            menuItems={useInterviewCardMenuItems}
            onEdit={onEdit}
          />
        ) : (
          <InterviewList
            interviews={loading ? undefined : interviews}
            type="upcoming"
            menuItems={useInterviewCardMenuItems}
          />
        )}
      </div>
    </>
  );
}

function InterviewsComponent(props: RichBlockProps<InterviewsData>) {
  const guideContext = useGuideIdContext();
  const guideId = guideContext?.guideId;

  if (!guideId) return <PlaceholderInterviewCard />;

  return <MessageInterviewList {...props} guideId={guideId} />;
}

export const {
  $createInterviewsNode,
  $isInterviewsNode,
  INSERT_INTERVIEWS_COMMAND,
  InterviewsNode,
  InterviewsPlugin,
  interviewsModule,
} = createRichBlock<InterviewsName, InterviewsData>({
  name: "interviews",
  defaultData: DEFAULT_DATA,
  RenderComponent: InterviewsComponent,
  useInsertOption(_, { INSERT_COMMAND }) {
    const [editor] = useLexicalComposerContext();
    const { timezone } = useCandidateTimezoneContext();

    return useMemo(
      () => ({
        icon: atlasCalendarAlt,
        label: "Interview schedule",
        targets: {
          insert: INSERT_TARGETS.INTERVIEW_SCHEDULE,
          fixedToolbar: { slot: "interviews" },
        },
        onTrigger: () =>
          editor.dispatchCommand(INSERT_COMMAND, {
            ...DEFAULT_DATA,
            requesterTimezone: timezone ?? DEFAULT_DATA.requesterTimezone,
          }),
      }),
      [editor, INSERT_COMMAND, timezone]
    );
  },
});
